import React from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Chip,
  Divider,
  IconButton,
  Paper,
  DialogTitle,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DragHandleIcon from '@mui/icons-material/DragHandle';

type ChangeType = 'added' | 'removed' | 'unchanged';

interface ContentSection {
  id: string;
  type: ChangeType;
  text: string;
}

interface VersionInfo {
  label: string;
  date: string;
  isCurrent?: boolean;
}

interface VersionNote {
  version: string;
  isCurrent?: boolean;
  note: string;
}

interface VersionComparisonDialogProps {
  open: boolean;
  onClose: () => void;
  previous: VersionInfo;
  current: VersionInfo;
  sections: ContentSection[];
  summary: string;
  versionNotes: VersionNote[];
}

const SectionRow: React.FC<{ section: ContentSection }> = ({ section }) => {
  const chipConfig: Record<
    ChangeType,
    { label: string; icon: React.ReactNode; className: string }
  > = {
    added: {
      label: 'Added',
      icon: <AddIcon sx={{ fontSize: 12 }} />,
      className: 'section-chip--added',
    },
    removed: {
      label: 'Removed',
      icon: <RemoveIcon sx={{ fontSize: 12 }} />,
      className: 'section-chip--removed',
    },
    unchanged: {
      label: 'Unchanged',
      icon: <DragHandleIcon sx={{ fontSize: 12 }} />,
      className: 'section-chip--unchanged',
    },
  };

  const config = chipConfig[section.type];

  const htmlToPlainText = (html: string): string => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
  };

  return (
    <Box className={`section-row section-row--${section.type}`}>
      <Box className="section-row-chip-wrap">
        <Chip
          icon={config.icon as React.ReactElement}
          label={config.label}
          size="small"
          className={`section-chip ${config.className}`}
        />
      </Box>
      <Typography variant="body2" className="section-row-text">
        {htmlToPlainText(section.text)}
      </Typography>
    </Box>
  );
};

const VersionCard: React.FC<{ info: VersionInfo }> = ({ info }) => (
  <Paper variant="outlined" className="version-card">
    <Box className="version-card-inner">
      <Box display="flex" alignItems="center" gap={3}>
        {info.isCurrent && (
          <Chip label="Current" size="small" className="version-card-badge" />
        )}
        {!info.isCurrent && <Chip label="Previous" size="small" />}
        <Typography variant="subtitle1" className="version-card-title">
          {info.label}
        </Typography>
      </Box>
      <Typography variant="caption" className="version-card-date">
        {info.date}
      </Typography>
    </Box>
  </Paper>
);

export const VersionComparisonDialog: React.FC<
  VersionComparisonDialogProps
> = ({ open, onClose, previous, current, sections, summary, versionNotes }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="sm"
    fullWidth
    classes={{ paper: 'vcd-paper' }}
  >
    <DialogTitle sx={{ m: 0, px: 5 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h6" className="vcd-title-heading">
            Version Comparison
          </Typography>
          <Typography variant="caption" className="vcd-title-sub">
            Comparing changes between {previous.label} and {current.label}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" className="vcd-title-close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </DialogTitle>

    <Divider />

    <DialogContent className="vcd-content mt-2">
      <Box className="vcd-cards-row">
        <VersionCard info={previous} />
        <VersionCard info={current} />
      </Box>

      <Box className="vcd-section">
        <Box className="vcd-changes-card">
          <Typography variant="subtitle2" className="vcd-section-heading">
            Content Changes
          </Typography>

          <Box className="vcd-legend">
            <Box className="vcd-legend-item">
              <span className="vcd-legend-dot vcd-legend-dot--added" />
              <Typography variant="caption">Added</Typography>
            </Box>
            <Box className="vcd-legend-item">
              <span className="vcd-legend-dot vcd-legend-dot--removed" />
              <Typography variant="caption">Removed</Typography>
            </Box>
            <Box className="vcd-legend-item">
              <span className="vcd-legend-dot vcd-legend-dot--unchanged" />
              <Typography variant="caption">Unchanged</Typography>
            </Box>
          </Box>

          <Box className="vcd-sections-list">
            {sections.map((s) => (
              <SectionRow key={s.id} section={s} />
            ))}
          </Box>
        </Box>

        <Typography variant="caption" className="vcd-summary">
          {summary}
        </Typography>
      </Box>

      <Box className="vcd-section">
        <Typography variant="subtitle2" className="vcd-section-heading">
          Version Notes
        </Typography>
        <Box className="vcd-notes">
          {versionNotes.map((n) => (
            <Paper key={n.version} variant="outlined" className="vcd-note">
              <Chip
                label={n.version}
                size="small"
                className={`vcd-note-chip ${
                  n.isCurrent ? 'vcd-note-chip--current' : ''
                }`}
              />
              <Typography variant="body2" className="vcd-note-text">
                {n.note}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Box>
    </DialogContent>
  </Dialog>
);
